import React, { useState, useEffect } from 'react';
import './GetQuote.css';

import { FaUser, FaEnvelope, FaPhone, FaHome, FaMapMarkerAlt, FaBuilding, FaSolarPanel, FaBatteryThreeQuarters, FaCalendarAlt, FaComment, FaPlug, FaAngleDown, FaDollarSign } from 'react-icons/fa';
import { Heading } from '../../../components';

import axios from "axios";


const API_PATH = 'https://sunspot-react.asdesignsgalaxy.com/api/phpmailer/quote.php';

const EMAIL_REGEX = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
const PHONE_REGEX = /^[+]?[(]?[0-9]{1,4}[)]?[-\s./0-9]*$/;

const SolarQuote = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    propertyType: '',
    address: '',
    roofType: '',
    roofSize: '',
    electricityBill: '',
    systemType: '',
    batteryInterest: '',
    timeline: '',
    pricingPlan: '',
    comments: ''
  });

  const [mailSent, setMailSent] = useState(false);
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (mailSent) {
      const timer = setTimeout(() => setMailSent(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [mailSent]);

  const handleSubmitForm = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const validationErrors = [];
      
      if (!formData.name.trim()) validationErrors.push('Full name is required');
      if (!formData.email.trim()) {
        validationErrors.push('Email is required');
      } else if (!EMAIL_REGEX.test(formData.email)) {
        validationErrors.push('Invalid email format');
      }
      if (!formData.phone.trim()) {
        validationErrors.push('Phone number is required');
      } else if (!PHONE_REGEX.test(formData.phone)) {
        validationErrors.push('Invalid phone number format');
      }
      if (!formData.propertyType) validationErrors.push('Property type is required');
      if (!formData.address.trim()) validationErrors.push('Address is required');
      if (!formData.roofType) validationErrors.push('Roof type is required');
      if (!formData.electricityBill.trim()) validationErrors.push('Electricity bill information is required');
      if (!formData.pricingPlan) validationErrors.push('Pricing plan is required');

      if (validationErrors.length > 0) {
        throw new Error(validationErrors.join(' | '));
      }

      const postData = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        postData.append(key, value);
      });

      const response = await axios.post(API_PATH, postData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (!response.data.sent) {
        throw new Error(response.data.message || 'Form submission failed');
      }

      setMailSent(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        propertyType: '',
        address: '',
        roofType: '',
        roofSize: '',
        electricityBill: '',
        systemType: '',
        batteryInterest: '',
        timeline: '',
        pricingPlan: '',
        comments: ''
      });
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'An error occurred');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <section className="custom-form solar-quote">
      <div className="box-container">
        <div className="content">
          <form className="dark-form" onSubmit={handleSubmitForm}>
            <Heading title={'Solar Energy Quote'} subTitle={'Get Your Free Consultation'} linear sideHeading />

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="name"><FaUser /></label>
                <input
                  type="text"
                  name="name"
                  className="box"
                  id="name"
                  placeholder="Full Name"
                  value={formData.name}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="email"><FaEnvelope /></label>
                <input
                  type="email"
                  name="email"
                  className="box"
                  id="email"
                  placeholder="Email Address"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="phone"><FaPhone /></label>
                <input
                  type="tel"
                  name="phone"
                  className="box"
                  id="phone"
                  placeholder="Phone Number"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="propertyType"><FaBuilding /></label>
                  <select
                    className="select-box"
                    id="propertyType"
                    name="propertyType"
                    value={formData.propertyType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Type of Property</option>
                    <option value="residential">Residential</option>
                    <option value="commercial">Commercial</option>
                    <option value="industrial">Industrial</option>
                    <option value="agricultural">Agricultural</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="roofType"><FaHome /></label>
                  <select
                    className="select-box"
                    id="roofType"
                    name="roofType"
                    value={formData.roofType}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Roof Type</option>
                    <option value="asphalt">Asphalt Shingles</option>
                    <option value="tile">Tile</option>
                    <option value="metal">Metal</option>
                    <option value="flat">Flat Roof</option>
                    <option value="other">Other</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="input-field">
                <label htmlFor="roofSize"><FaHome /></label>
                <input
                  type="text"
                  name="roofSize"
                  className="box"
                  id="roofSize"
                  placeholder="Roof Size/Area (sq ft)"
                  value={formData.roofSize}
                  onChange={handleInputChange}
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="input-box">
              <div className="input-field">
                <label htmlFor="address"><FaMapMarkerAlt /></label>
                <input
                  type="text"
                  name="address"
                  className="box"
                  id="address"
                  placeholder="Installation Address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="input-field">
                <label htmlFor="electricityBill"><FaPlug /></label>
                <input
                  type="text"
                  name="electricityBill"
                  className="box"
                  id="electricityBill"
                  placeholder="Avg Monthly Electricity Bill ($)"
                  value={formData.electricityBill}
                  onChange={handleInputChange}
                  required
                  disabled={isSubmitting}
                />
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="systemType"><FaSolarPanel /></label>
                  <select
                    className="select-box"
                    id="systemType"
                    name="systemType"
                    value={formData.systemType}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Type of System</option>
                    <option value="grid-tied">Grid-Tied</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="off-grid">Off-Grid</option>
                    <option value="not-sure">Not Sure</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-box">
              <div className="select">
                <div className="select-container">
                  <label htmlFor="batteryInterest"><FaBatteryThreeQuarters /></label>
                  <select
                    className="select-box"
                    id="batteryInterest"
                    name="batteryInterest"
                    value={formData.batteryInterest}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Battery Storage Interest?</option>
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                    <option value="maybe">Considering</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="timeline"><FaCalendarAlt /></label>
                  <select
                    className="select-box"
                    id="timeline"
                    name="timeline"
                    value={formData.timeline}
                    onChange={handleInputChange}
                    disabled={isSubmitting}
                  >
                    <option value="">Timeline to Install</option>
                    <option value="immediately">Immediately</option>
                    <option value="1-3-months">1-3 Months</option>
                    <option value="3-6-months">3-6 Months</option>
                    <option value="6+months">6+ Months</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>

              <div className="select">
                <div className="select-container">
                  <label htmlFor="pricingPlan"><FaDollarSign /></label>
                  <select
                    className="select-box"
                    id="pricingPlan"
                    name="pricingPlan"
                    value={formData.pricingPlan}
                    onChange={handleInputChange}
                    required
                    disabled={isSubmitting}
                  >
                    <option value="">Pricing Plan</option>
                    <option value="basic">Basic Plan</option>
                    <option value="advanced">Advanced Plan</option>
                    <option value="premium">Premium Plan</option>
                  </select>
                  <div className="icon-container"><FaAngleDown className="icon" /></div>
                </div>
              </div>
            </div>

            <div className="input-field text-area">
              <label htmlFor="comments"><FaComment /></label>
              <textarea
                cols="30"
                rows="5"
                name="comments"
                className="box"
                id="comments"
                placeholder="Comments or Special Requirements"
                value={formData.comments}
                onChange={handleInputChange}
                disabled={isSubmitting}
              ></textarea>
            </div>

            <button type="submit" className="btn" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Get Your Free Quote'}
            </button>

            {error && (
              <div className="alert error">
                {error.split(' | ').map((msg, i) => (
                  <span key={i}>{msg}</span>
                ))}
              </div>
            )}
            {mailSent && <div className="alert success"><span>Quote Request Submitted Successfully!</span></div>}
          </form>
        </div>
      </div>
    </section>
  );
};

export default SolarQuote;